/**
 * ============================================================================
 * BUNDLE INTEGRITY — Layer 4: Cart-Side Orphan Sweeper
 * ============================================================================
 *
 * Purpose: detect and remove cart items that are bundle children
 * (carrying the properties._parent_product_id line item property) but have
 * no legitimate bundle parent in the same cart. A legitimate parent is an
 * item that (a) carries properties._bundle_product with a matching value
 * AND (b) is itself NOT a lock-cart-tagged variant.
 *
 * This is the second line of defense behind lock-cart.js. It catches any
 * cart state that should never have existed, regardless of how it got
 * there:
 *   - Direct multi-item /cart/add exploits that bypass an earlier defense
 *     (the pattern behind order DTC344942)
 *   - App or script interactions that remove a bundle parent but leave its
 *     children behind
 *   - Customers who manually remove only the parent via the cart API
 *   - "Fake parent" requests where the declared bundle parent is itself a
 *     locked $0 child variant
 *
 * Runs on every page load (deferred). If orphans are found, removes them
 * via /cart/update.js and reloads the cart page when the user is on it.
 * ============================================================================
 */

(function () {
  'use strict';

  const ORPHAN_REMOVED_MESSAGE =
    'Some items in your cart were part of an incomplete bundle and have been removed.';

  function isBlocked(variantId) {
    if (!window.LOCK_CART || !window.LOCK_CART.blockedVariants) return false;
    return window.LOCK_CART.blockedVariants.indexOf(String(variantId)) !== -1;
  }

  // Wait (briefly) for lock-cart.js to populate its blocked-variant list,
  // so the "fake parent on locked variant" branch evaluates correctly. If
  // it never becomes ready, proceed anyway — the basic orphan case (child
  // with no _bundle_product line anywhere in cart) doesn't need it.
  function waitForLockCart(callback, attemptsLeft) {
    if (window.LOCK_CART && window.LOCK_CART.ready) { callback(); return; }
    if (attemptsLeft <= 0) { callback(); return; }
    setTimeout(function () { waitForLockCart(callback, attemptsLeft - 1); }, 200);
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function findOrphans(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return [];

    const legitParentIds = Object.create(null);
    cart.items.forEach(function (item) {
      if (item.properties && item.properties._bundle_product) {
        if (!isBlocked(item.variant_id)) {
          legitParentIds[item.properties._bundle_product] = true;
        }
      }
    });

    const orphanKeys = [];
    cart.items.forEach(function (item) {
      if (!item.properties) return;

      // Orphan child: declares a parent that isn't legitimately present.
      if (item.properties._parent_product_id &&
          !legitParentIds[item.properties._parent_product_id]) {
        orphanKeys.push(item.key);
        return;
      }

      // Fake parent: declares itself the bundle parent while being a
      // locked (intended-as-component) variant.
      if (item.properties._bundle_product && isBlocked(item.variant_id)) {
        orphanKeys.push(item.key);
      }
    });

    return orphanKeys;
  }

  function removeOrphans(orphanKeys) {
    const updates = {};
    orphanKeys.forEach(function (key) { updates[key] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function isOnCartPage() {
    const p = window.location.pathname;
    return p === '/cart' || p.indexOf('/cart/') === 0;
  }

  function sweep() {
    fetchCart()
      .then(function (cart) {
        const orphans = findOrphans(cart);
        if (orphans.length === 0) return null;
        console.warn('[BUNDLE-INTEGRITY] Removing ' + orphans.length + ' orphan bundle item(s)');
        return removeOrphans(orphans).then(function () {
          if (isOnCartPage()) {
            try { alert(ORPHAN_REMOVED_MESSAGE); } catch (e) { /* ignore */ }
            window.location.reload();
          }
        });
      })
      .catch(function (err) {
        console.warn('[BUNDLE-INTEGRITY] Sweep failed', err);
      });
  }

  function start() {
    waitForLockCart(sweep, 25); // ~5s max wait
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
